/* eslint-disable react-refresh/only-export-components */

import {
  FC,
  PropsWithChildren,
  useReducer,
  createContext,
  useMemo,
  useContext,
} from 'react';

import dayjs from 'dayjs';
import { useStorage } from 'utils/helpers';
import { STORAGE_TYPE } from 'utils/helpers/useStorage';

export type TimezoneSource = 'account' | 'local';

export type TimezoneState = {
  accountTimezone: string;
  localTimezone: string;
  activeSource: TimezoneSource;
};

export type TimezoneAction =
  | { type: 'setAccountTimezone'; payload: string }
  | { type: 'setActiveSource'; payload: TimezoneSource };

type TimezoneContextValue = {
  state: TimezoneState;
  dispatch: React.Dispatch<TimezoneAction>;
};

export const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export const timezoneReducer = (
  state: TimezoneState,
  action: TimezoneAction,
): TimezoneState => {
  switch (action.type) {
    case 'setAccountTimezone':
      return {
        ...state,
        accountTimezone: action.payload,
      };

    case 'setActiveSource':
      return {
        ...state,
        activeSource: action.payload,
      };

    default:
      return state;
  }
};

const TimezoneProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getItem } = useStorage();

  const prefersLocal =
    getItem('useLocalTimezone', STORAGE_TYPE.local) === 'true';

  const [state, dispatch] = useReducer(timezoneReducer, {
    accountTimezone: import.meta.env.VITE_APP_TIMEZONE,
    localTimezone: dayjs.tz.guess(),
    activeSource: prefersLocal ? 'local' : 'account',
  });

  return (
    <TimezoneContext.Provider value={{ state, dispatch }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezoneProviderContext = () => {
  const ctx = useContext(TimezoneContext);

  if (!ctx) {
    throw new Error('useTimezoneContext must be used inside TimezoneProvider');
  }

  return ctx;
};

export type UseTimezoneOptions = {
  source?: 'account' | 'local';
};

export function useTimezone(options?: UseTimezoneOptions) {
  const { state } = useTimezoneProviderContext();

  const source = options?.source ?? state.activeSource;

  const timezone = useMemo(() => {
    return source === 'local' ? state.localTimezone : state.accountTimezone;
  }, [source, state.localTimezone, state.accountTimezone]);

  return {
    timezone,
    source,
    isLocal: source === 'local',
    isAccount: source === 'account',
    localTimezone: state.localTimezone,
    accountTimezone: state.accountTimezone,
  };
}

export default TimezoneProvider;
